import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaModeloStruct } from '@shared/services/structs/formula/formula-modelo.struct';
import { FormulaCardModoEnum } from '@shared/enums/motor-fiscal/formula-card-modo.enum';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { modeloAtivo, parseDataFormula, rotuloModeloFormula } from '../utils/formula-modelo.util';
import { FormulaModeloCardComponent } from '../formula-modelo-card/formula-modelo-card.component';

/**
 * Seção de troca de modelo.
 * - modo 'editor': apenas emite a seleção local, não persiste.
 * - modo 'listagem': pede à página proprietária a alteração global do modelo da empresa (com confirmação).
 */
@Component({
    selector: 'app-alterar-modelo-section',
    standalone: true,
    imports: [CommonModule, MatIconModule, MatDialogModule, FormulaModeloCardComponent],
    templateUrl: './alterar-modelo-section.component.html',
    styleUrl: './alterar-modelo-section.component.css',
})
export class AlterarModeloSectionComponent implements OnChanges {
    @Input({ required: true }) formula!: FormulaCatalogoItemStruct;
    @Input() modo: FormulaCardModoEnum = FormulaCardModoEnum.Listagem;
    @Input() modeloSelecionado: string | null = null;
    /** Editor: emite o modelo escolhido (seleção local). */
    @Output() modeloSelecionadoChange = new EventEmitter<FormulaModeloStruct>();
    /** Listagem: solicita a alteração global à página proprietária. */
    @Output() modeloEmpresaSelecionado = new EventEmitter<FormulaModeloStruct>();

    /** Modelo destacado visualmente na seção. */
    public selecionadoLocal: string | null = null;

    public get isModoEditor(): boolean {
        return this.modo === FormulaCardModoEnum.Editor;
    }

    public get isModoListagem(): boolean {
        return this.modo === FormulaCardModoEnum.Listagem;
    }

    constructor(private dialog: MatDialog) {}

    ngOnChanges(): void {
        this.selecionadoLocal = this.modeloSelecionado ?? this.modeloAtualId;
    }

    public get modelos(): FormulaModeloStruct[] {
        const porFamilia = new Map<string, FormulaModeloStruct[]>();
        for (const modelo of this.formula.modelos ?? []) {
            const familia = porFamilia.get(modelo.chaveFormulaModelo) ?? [];
            familia.push(modelo);
            porFamilia.set(modelo.chaveFormulaModelo, familia);
        }
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        return [...porFamilia.values()].map(versoes => {
            const ordenadas = [...versoes].sort((a, b) => (a.dataVigenciaInicio ?? '').localeCompare(b.dataVigenciaInicio ?? ''));
            return ordenadas.find(modelo => {
                const inicio = parseDataFormula(modelo.dataVigenciaInicio);
                const fim = parseDataFormula(modelo.dataVigenciaFim);
                return (!inicio || inicio <= hoje) && (!fim || fim >= hoje);
            }) ?? ordenadas.find(modelo => !modelo.dataVigenciaInicio || parseDataFormula(modelo.dataVigenciaInicio)! > hoje)
                ?? ordenadas.at(-1)!;
        });
    }

    public versoesDaFamilia(modelo: FormulaModeloStruct): FormulaModeloStruct[] {
        return (this.formula.modelos ?? [])
            .filter(item => item.chaveFormulaModelo === modelo.chaveFormulaModelo)
            .sort((a, b) => (a.dataVigenciaInicio ?? '').localeCompare(b.dataVigenciaInicio ?? ''));
    }

    /** Id do modelo atualmente em uso pelo sistema para esta fórmula. */
    private get modeloAtualId(): string | null {
        return modeloAtivo(this.formula)?.chaveFormulaModelo ?? null;
    }

    /** Id do modelo padrão (global da empresa ou padrão do sistema) — marca o badge "Padrão" no editor. */
    public get idModeloPadrao(): string | null {
        return this.modeloAtualId;
    }

    public rotuloModelo(modelo: FormulaModeloStruct): string {
        return rotuloModeloFormula(this.formula, modelo);
    }

    public selecionar(modelo: FormulaModeloStruct): void {
        if (this.modo === FormulaCardModoEnum.Editor) {
            this.selecionadoLocal = modelo.chaveFormulaModelo;
            this.modeloSelecionadoChange.emit(modelo);
            return;
        }

        // Listagem: persiste globalmente com confirmação.
        // Selecionar o que já está em uso não exige confirmação.
        if (modelo.chaveFormulaModelo === this.modeloAtualId) {
            this.selecionadoLocal = modelo.chaveFormulaModelo;
            return;
        }

        // Seleção visual provisória até a confirmação.
        this.selecionadoLocal = modelo.chaveFormulaModelo;
        const ref = this.dialog.open(ConfirmModalComponent, {
            width: '460px',
            data: {
                title: 'Alterar modelo padrão',
                description: this.descricaoAlterarModeloPadrao,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        ref.afterClosed().subscribe(result => {
            if (result?.confirm) {
                this.modeloEmpresaSelecionado.emit(modelo);
            } else {
                // Cancelou: volta para o modelo em uso, mas a seção permanece aberta.
                this.selecionadoLocal = this.modeloAtualId;
            }
        });
    }

    private get descricaoAlterarModeloPadrao(): string {
        return 'Este modelo passará a ser o padrão para todas as configurações que herdam o modelo global. Configurações com modelo sobrescrito localmente não são afetadas.';
    }
}
